import { supabase } from '../lib/supabase';

const BUCKET_NAME = 'newsletter-images';
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

const imageStorageService = {
  /**
   * Validate image file before upload
   */
  validateImage(file) {
    if (!file) {
      throw new Error('No file provided');
    }

    if (!ALLOWED_TYPES?.includes(file?.type)) {
      throw new Error('Invalid file type. Only PNG, JPG, and WebP are allowed.');
    }

    if (file?.size > MAX_FILE_SIZE) {
      throw new Error('File size exceeds 10MB limit.');
    }

    return true;
  },

  /**
   * Upload image to Supabase Storage
   */
  async uploadImage(file, folder = 'uploads') {
    try {
      this.validateImage(file);

      const timestamp = Date.now();
      const fileExt = file?.name?.split('.')?.pop();
      const fileName = `${timestamp}-${Math.random()?.toString(36)?.substring(7)}.${fileExt}`;
      const filePath = `${folder}/${fileName}`;

      const { data, error } = await supabase?.storage
        ?.from(BUCKET_NAME)
        ?.upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) throw error;

      return {
        path: data?.path,
        url: this.getPublicUrl(data?.path)
      };
    } catch (error) {
      console.error('Upload error:', error);
      throw new Error(error?.message || 'Failed to upload image');
    }
  },

  /**
   * Get public URL for uploaded image
   */
  getPublicUrl(filePath) {
    const { data } = supabase?.storage
      ?.from(BUCKET_NAME)
      ?.getPublicUrl(filePath);

    return data?.publicUrl;
  },

  /**
   * Delete image from storage
   */
  async deleteImage(filePath) {
    try {
      const { error } = await supabase?.storage
        ?.from(BUCKET_NAME)
        ?.remove([filePath]);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Delete error:', error);
      throw new Error(error?.message || 'Failed to delete image');
    }
  },

  /**
   * List images in a folder
   */
  async listImages(folder = 'uploads', options = {}) {
    try {
      const { data, error } = await supabase?.storage
        ?.from(BUCKET_NAME)
        ?.list(folder, {
          limit: options?.limit || 100,
          offset: options?.offset || 0,
          sortBy: { column: 'created_at', order: 'desc' }
        });

      if (error) throw error;

      return data?.map(file => ({
        name: file?.name,
        path: `${folder}/${file?.name}`,
        url: this.getPublicUrl(`${folder}/${file?.name}`),
        size: file?.metadata?.size,
        createdAt: file?.created_at
      })) || [];
    } catch (error) {
      console.error('List error:', error);
      throw new Error(error?.message || 'Failed to list images');
    }
  }
};

export default imageStorageService;